import { ChevronUp, MessageSquare, User } from 'lucide-react'

import { useEffect, useState } from 'react'

import { useFetchChatSessions } from '@/hooks/features/chat/useFetchChatSessions'
import { useFetchChatTranscript } from '@/hooks/features/chat/useFetchChatTranscript'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Pagination } from '@/components/ui/pagination-controls'

import { PageHeader } from '@/components/atoms'
import { cn } from '@/lib/utils'
import { ChatModel } from '@/types/model/Chat'

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

const formatDay = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' })

export const ChatSessions = () => {
  const { loading, data, pagination, setPage } = useFetchChatSessions()
  const { messages, hasMore, isLoading, isLoadingMore, fetchTranscript, loadOlder, reset } =
    useFetchChatTranscript()
  const [activeSession, setActiveSession] = useState<ChatModel.ChatSession | null>(null)

  const openSession = (session: ChatModel.ChatSession) => {
    if (session.session_id === activeSession?.session_id) return
    setActiveSession(session)
    reset()
    fetchTranscript(session.session_id)
  }

  // Auto-select the first session once the list loads, so the right pane isn't empty.
  useEffect(() => {
    if (!activeSession && data.length > 0) {
      openSession(data[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <div>
      <PageHeader
        size="large"
        title="Chat sessions"
        description="Read-only view of customer conversations with the storefront AI shopping assistant."
        badge={<Badge tone="info">Free trial</Badge>}
      />

      {/* Phones stack the list above the transcript; from md the panes sit side by side. */}
      <Card className="flex h-[calc(100vh-220px)] min-h-[500px] flex-col overflow-hidden p-0 md:flex-row">
        {/* Left pane: session list */}
        <div className="flex max-h-[40%] min-h-0 w-full shrink-0 flex-col border-b border-border md:max-h-none md:max-w-xs md:border-b-0 md:border-r">
          <div className="flex-1 overflow-y-auto">
            {loading && data.length === 0 ? (
              <div className="p-4 text-sm text-text-2">Loading sessions…</div>
            ) : data.length === 0 ? (
              <div className="p-4 text-sm text-text-2">No chat sessions yet.</div>
            ) : (
              data.map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => openSession(session)}
                  className={cn(
                    'flex w-full items-start gap-3 border-b border-border-subtle px-4 py-3 text-left transition-colors hover:bg-subdued',
                    activeSession?.session_id === session.session_id && 'bg-selected hover:bg-selected'
                  )}
                >
                  <Avatar className="size-9 shrink-0">
                    <AvatarFallback>
                      {(session.customer?.name || 'G').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-ink">
                        {session.customer?.name || 'Guest'}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-text-2">
                        {formatTime(session.last_activity_at)}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-text-2">
                        {session.last_message_role === 'model' ? 'Bot: ' : ''}
                        {session.last_message}
                      </span>
                      <Badge tone="soft" className="shrink-0 tabular-nums">
                        {session.message_count}
                      </Badge>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-border-subtle p-2">
            <Pagination.Buttons
              currentPage={pagination.currentPage}
              totalItems={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* Right pane: transcript */}
        <div className="flex min-h-0 flex-1 flex-col">
          {!activeSession ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-text-2">
              <MessageSquare className="size-8" />
              <span className="text-sm">Select a conversation to view messages</span>
            </div>
          ) : (
            <>
              <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
                <Avatar className="size-8">
                  <AvatarFallback>
                    {(activeSession.customer?.name || 'G').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-sm font-medium text-ink">{activeSession.customer?.name || 'Guest'}</div>
                  <div className="text-xs text-text-2">
                    {activeSession.customer?.email || activeSession.customer?.phone || activeSession.system_uid}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {isLoading ? (
                  <div className="py-10 text-center text-sm text-text-2">Loading conversation…</div>
                ) : (
                  <div className="space-y-4">
                    {hasMore && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadOlder(activeSession.session_id)}
                          disabled={isLoadingMore}
                        >
                          <ChevronUp />
                          {isLoadingMore ? 'Loading…' : 'Load older messages'}
                        </Button>
                      </div>
                    )}

                    {messages.map((message, index) => {
                      const showDayDivider =
                        index === 0 ||
                        formatDay(message.created_at) !== formatDay(messages[index - 1].created_at)

                      return (
                        <div key={message.id}>
                          {showDayDivider && (
                            <div className="my-3 flex justify-center">
                              <span className="rounded-sm bg-muted px-2 py-0.5 text-xs text-text-2">
                                {formatDay(message.created_at)}
                              </span>
                            </div>
                          )}
                          <div
                            className={cn(
                              'flex gap-2',
                              message.role === 'user' ? 'justify-start' : 'justify-end'
                            )}
                          >
                            {message.role === 'user' && (
                              <Avatar className="mt-1 size-6 shrink-0">
                                <AvatarFallback>
                                  <User className="size-3.5" />
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <div
                              className={cn(
                                'max-w-[70%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                                message.role === 'user'
                                  ? 'rounded-tl-sm bg-fill text-ink'
                                  : 'rounded-tr-sm bg-primary text-primary-foreground'
                              )}
                            >
                              {message.content}
                              <div
                                className={cn(
                                  'mt-1 text-xs opacity-60',
                                  message.role === 'user' ? 'text-left' : 'text-right'
                                )}
                              >
                                {formatTime(message.created_at)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
